import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertComponent, AlertData, AlertPerson } from '../alert/alert.component';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  constructor(private dialog: MatDialog) {}

  /**
   * Shows a custom alert dialog that mimics the browser's alert() function
   * @param message The message to display
   * @param title Optional title for the dialog
   * @param confirmText Optional text for the confirm button
   * @returns Promise that resolves when user clicks OK or closes dialog
   */
  show(
    message: string, 
    title?: string, 
    confirmText?: string,
    details?: string
  ): Promise<boolean> {
    const dialogRef: MatDialogRef<AlertComponent> = this.dialog.open(AlertComponent, {
      data: { 
        message, 
        title, 
        confirmText,
        details,
        isConfirm: false 
      } as AlertData,
      disableClose: true, // Prevent closing by clicking backdrop
      autoFocus: true,
      restoreFocus: true,
      panelClass: 'custom-alert-dialog',
      maxWidth: '90vw',
      maxHeight: '90vh'
    });

    // Return a promise that resolves when dialog is closed
    return dialogRef.afterClosed().toPromise();
  }

  /**
   * Shows a confirmation dialog with Yes/No buttons
   * @param message The message to display
   * @param title Optional title for the dialog
   * @param confirmText Optional text for the confirm button (default: "Yes")
   * @param cancelText Optional text for the cancel button (default: "No")
   * @returns Promise that resolves to true if user confirms, false if they cancel
   */
  confirm(
    message: string,
    title?: string,
    confirmText?: string,
    cancelText?: string,
    details?: string,
    person?: AlertPerson
  ): Promise<boolean> {
    const dialogRef: MatDialogRef<AlertComponent> = this.dialog.open(AlertComponent, {
      data: { 
        message, 
        title, 
        confirmText, 
        cancelText,
        details,
        person,
        isConfirm: true 
      } as AlertData,
      disableClose: true, // Prevent closing by clicking backdrop
      autoFocus: true,
      restoreFocus: true,
      panelClass: 'custom-alert-dialog',
      maxWidth: '90vw',
      maxHeight: '90vh'
    });

    // Return a promise that resolves when dialog is closed
    return dialogRef.afterClosed().toPromise();
  }

  /**
   * Convenience method that mimics the browser alert() - blocks execution
   * @param message The message to display
   */
  async alert(message: string): Promise<void> {
    await this.show(message);
  }

  /**
   * Shows an error alert with error styling
   * @param message The error message to display
   */
  async error(message: string, details?: string): Promise<void> {
    await this.show(message, 'Error', 'OK', details);
  }

  /**
   * Shows a success alert with success styling
   * @param message The success message to display
   */
  async success(message: string, details?: string): Promise<void> {
    await this.show(message, 'Success', 'OK', details);
  }

  /**
   * Shows a warning alert with warning styling
   * @param message The warning message to display
   */
  async warning(message: string, details?: string): Promise<void> {
    await this.show(message, 'Warning', 'OK', details);
  }

  /**
   * Extracts useful error details from an HTTP error or generic error object.
   * @param error The error object returned from a failed request
   */
  getErrorDetails(error: any): string | undefined {
    if (!error) return undefined;

    console.log('Extracting error details from:', error);

    const parts: string[] = [];

    const detail =
      error?.error?.detail ??
      error?.error?.message ??
      error?.error?.error ??
      error?.message;
    if (typeof detail === 'string' && detail.trim()) {
      parts.push(`Details: ${detail.trim()}`);
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      parts.push(`Message: ${error.message.trim()}`);
    }

    if (typeof error?.name === 'string' && error.name.trim()) {
      parts.push(`Type: ${error.name.trim()}`);
    }

    const status = error?.status;
    const statusText = error?.statusText;
    if (status || statusText) {
      parts.push(`Status: ${(status ?? '').toString().trim()}${statusText ? ` ${statusText}` : ''}`.trim());
    }

    if (typeof error?.url === 'string' && error.url.trim()) {
      parts.push(`URL: ${error.url.trim()}`);
    }

    if (typeof error?.error === 'string' && error.error.trim()) {
      parts.push(`Server error: ${error.error.trim()}`);
    } else if (error?.error != null) {
      try {
        parts.push(`Server error:\n${JSON.stringify(error.error, null, 2)}`);
      } catch {
        parts.push('Server error: [unserializable]');
      }
    }

    const seen = new WeakSet<object>();
    const safeStringify = (value: any): string | undefined => {
      try {
        return JSON.stringify(
          value,
          (key, val) => {
            if (typeof val === 'object' && val !== null) {
              if (seen.has(val)) return '[Circular]';
              seen.add(val);
            }
            return val;
          },
          2
        );
      } catch {
        return undefined;
      }
    };

    const full = safeStringify(error);
    if (full) {
      parts.push(`Full error:\n${full}`);
    }

    return parts.length ? parts.join('\n\n') : undefined;
  }
}